import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CabinCrewLayout from '../layouts/CabinCrewLayout';
import { AuthContext } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft, ArrowRight, BookOpen, Brain, CheckCircle, 
  Lightbulb, Play, Volume2, ChevronDown, ChevronUp, Sparkles
} from 'lucide-react';

// Detaylı gramer dersleri
const GRAMMAR_LESSONS = {
  // A1 Level
  "present_simple": {
    name: "Present Simple (Geniş Zaman)",
    level: "A1",
    sections: [
      {
        title: "Ne Zaman Kullanılır?",
        content: `**Present Simple** (Geniş Zaman) şu durumlarda kullanılır:

• **Alışkanlıklar ve rutinler:** Her gün, her hafta tekrarlanan eylemler
• **Genel gerçekler:** Bilimsel gerçekler, değişmeyen durumlar
• **Programlar ve tarifeler:** Tren, uçak saatleri gibi

**Zaman zarfları:** always, usually, often, sometimes, rarely, never, every day/week/month`,
        examples: [
          { en: "I wake up at 7 AM every day.", tr: "Her gün sabah 7'de uyanırım." },
          { en: "The sun rises in the east.", tr: "Güneş doğudan doğar." },
          { en: "The flight leaves at 8 PM.", tr: "Uçuş saat 20:00'da kalkıyor." }
        ]
      },
      {
        title: "Olumlu Cümle Yapısı",
        content: `**Özne + Fiil (+ -s/-es 3. tekil şahıs için)**

• I/You/We/They + **fiil** (yalın hali)
• He/She/It + **fiil + s/es**

**Dikkat:** Üçüncü tekil şahıslarda (he, she, it) fiile **-s** veya **-es** eklenir.`,
        examples: [
          { en: "I **speak** English.", tr: "İngilizce konuşurum." },
          { en: "She **speaks** three languages.", tr: "O üç dil konuşur." },
          { en: "He **watches** TV every evening.", tr: "Her akşam TV izler." },
          { en: "The plane **flies** to London.", tr: "Uçak Londra'ya uçar." }
        ]
      },
      {
        title: "Olumsuz Cümle Yapısı",
        content: `**Özne + do/does + not + fiil**

• I/You/We/They + **do not (don't)** + fiil
• He/She/It + **does not (doesn't)** + fiil

**Önemli:** "doesn't" kullandığımızda fiile -s/-es EKLENMEZ!`,
        examples: [
          { en: "I **don't** like coffee.", tr: "Kahve sevmem." },
          { en: "She **doesn't** work on Sundays.", tr: "Pazar günleri çalışmaz." },
          { en: "They **don't** live in Istanbul.", tr: "İstanbul'da yaşamıyorlar." }
        ]
      },
      {
        title: "Soru Cümlesi Yapısı",
        content: `**Do/Does + Özne + fiil?**

• **Do** I/you/we/they + fiil?
• **Does** he/she/it + fiil?

**Önemli:** "Does" kullandığımızda fiile -s/-es EKLENMEZ!`,
        examples: [
          { en: "**Do** you speak English?", tr: "İngilizce konuşuyor musun?" },
          { en: "**Does** she work here?", tr: "O burada mı çalışıyor?" },
          { en: "**Do** they live in Ankara?", tr: "Ankara'da mı yaşıyorlar?" }
        ]
      }
    ]
  },
  
  "present_continuous": {
    name: "Present Continuous (Şimdiki Zaman)",
    level: "A1",
    sections: [
      {
        title: "Ne Zaman Kullanılır?",
        content: `**Present Continuous** şu durumlarda kullanılır:

• **Şu an yapılan eylemler:** Konuşma anında devam eden işler
• **Geçici durumlar:** Kısa süreli, geçici durumlar
• **Gelecek planlar:** Kesin yapılacak yakın gelecek planları

**Zaman zarfları:** now, right now, at the moment, currently, today, this week`,
        examples: [
          { en: "I am reading a book now.", tr: "Şu an kitap okuyorum." },
          { en: "She is staying at a hotel this week.", tr: "Bu hafta bir otelde kalıyor." },
          { en: "We are meeting tomorrow.", tr: "Yarın buluşuyoruz." }
        ]
      },
      {
        title: "Cümle Yapısı",
        content: `**Özne + am/is/are + fiil + -ing**

• I + **am** + verb-ing
• He/She/It + **is** + verb-ing
• You/We/They + **are** + verb-ing`,
        examples: [
          { en: "I **am working**.", tr: "Çalışıyorum." },
          { en: "She **is cooking** dinner.", tr: "Akşam yemeği pişiriyor." },
          { en: "They **are playing** football.", tr: "Futbol oynuyorlar." }
        ]
      },
      {
        title: "Olumsuz ve Soru",
        content: `**Olumsuz:** Özne + am/is/are + **not** + verb-ing
**Soru:** **Am/Is/Are** + özne + verb-ing?`,
        examples: [
          { en: "I **am not** sleeping.", tr: "Uyumuyorum." },
          { en: "**Is** she coming?", tr: "O geliyor mu?" },
          { en: "**Are** they waiting?", tr: "Bekliyorlar mı?" }
        ]
      }
    ]
  },

  "prepositions_basic": {
    name: "Prepositions (in, on, at)",
    level: "A1",
    sections: [
      {
        title: "IN - İçinde (Geniş alanlar ve zaman dilimleri)",
        content: `**IN** şu durumlarda kullanılır:

**YER için:**
• Ülkeler, şehirler: in Turkey, in Istanbul
• Kapalı alanlar: in the room, in the car, in the office
• Sınırlı alanlar: in the garden, in the park

**ZAMAN için:**
• Aylar: in January, in March
• Yıllar: in 2024, in 1990
• Mevsimler: in summer, in winter
• Günün bölümleri: in the morning, in the afternoon, in the evening`,
        examples: [
          { en: "I live **in** Istanbul.", tr: "İstanbul'da yaşıyorum." },
          { en: "She was born **in** 1995.", tr: "1995'te doğdu." },
          { en: "We have a meeting **in** the afternoon.", tr: "Öğleden sonra toplantımız var." },
          { en: "The keys are **in** my bag.", tr: "Anahtarlar çantamda." }
        ]
      },
      {
        title: "ON - Üzerinde (Yüzeyler ve belirli günler)",
        content: `**ON** şu durumlarda kullanılır:

**YER için:**
• Yüzeyler: on the table, on the floor, on the wall
• Ulaşım (büyük): on the bus, on the plane, on the train
• Katlar: on the first floor

**ZAMAN için:**
• Günler: on Monday, on Sunday
• Tarihler: on March 15th, on my birthday
• Özel günler: on Christmas Day, on New Year's Eve`,
        examples: [
          { en: "The book is **on** the table.", tr: "Kitap masanın üzerinde." },
          { en: "I have English class **on** Wednesdays.", tr: "Çarşamba günleri İngilizce dersim var." },
          { en: "We arrived **on** the 5th of May.", tr: "5 Mayıs'ta vardık." },
          { en: "There are pictures **on** the wall.", tr: "Duvarda resimler var." }
        ]
      },
      {
        title: "AT - Belirli nokta ve saatler",
        content: `**AT** şu durumlarda kullanılır:

**YER için:**
• Belirli noktalar: at the door, at the bus stop
• Adresler: at 123 Main Street
• Etkinlikler: at a party, at the meeting
• İşyerleri: at work, at school, at home

**ZAMAN için:**
• Saatler: at 5 o'clock, at noon, at midnight
• Öğünler: at breakfast, at lunch, at dinner
• Özel zamanlar: at night, at the weekend (İng.)`,
        examples: [
          { en: "I'll meet you **at** the airport.", tr: "Havalimanında buluşalım." },
          { en: "The meeting starts **at** 9 AM.", tr: "Toplantı saat 9'da başlıyor." },
          { en: "She is **at** work now.", tr: "Şu an işte." },
          { en: "We eat dinner **at** 7 PM.", tr: "Akşam yemeğini 7'de yeriz." }
        ]
      },
      {
        title: "Karşılaştırma Tablosu",
        content: `| Kullanım | IN | ON | AT |
|----------|-----|-----|-----|
| **Yer** | Ülkeler, şehirler, odalar | Yüzeyler, katlar | Noktalar, adresler |
| **Zaman** | Aylar, yıllar, mevsimler | Günler, tarihler | Saatler |
| **Örnek** | in the kitchen | on Monday | at 5 o'clock |`,
        examples: [
          { en: "I live **in** a house **on** Oak Street **at** number 42.", tr: "42 numaralı Oak Sokağı'ndaki bir evde yaşıyorum." },
          { en: "**In** summer, **on** weekends, **at** noon, we go to the beach.", tr: "Yazın, hafta sonları, öğlen plaja gideriz." }
        ]
      }
    ]
  },

  "past_simple": {
    name: "Past Simple (Geçmiş Zaman)",
    level: "A2",
    sections: [
      {
        title: "Ne Zaman Kullanılır?",
        content: `**Past Simple** şu durumlarda kullanılır:

• **Geçmişte biten eylemler:** Belirli bir zamanda yapılmış ve bitmiş işler
• **Geçmişteki alışkanlıklar:** Artık yapılmayan eski alışkanlıklar
• **Hikaye anlatımı:** Olayları sırayla anlatırken

**Zaman zarfları:** yesterday, last week/month/year, ago, in 2020, when I was young`,
        examples: [
          { en: "I visited Paris last summer.", tr: "Geçen yaz Paris'i ziyaret ettim." },
          { en: "She worked as a flight attendant for 5 years.", tr: "5 yıl kabin memuru olarak çalıştı." },
          { en: "They moved to London in 2019.", tr: "2019'da Londra'ya taşındılar." }
        ]
      },
      {
        title: "Düzenli Fiiller (Regular Verbs)",
        content: `Düzenli fiillere **-ed** eklenir:

• work → work**ed**
• play → play**ed**
• study → studi**ed** (y→i)
• stop → stopp**ed** (son harf iki kez)

**Telaffuz:** 
• /t/: worked, watched, stopped
• /d/: played, lived, studied
• /ɪd/: wanted, needed, visited`,
        examples: [
          { en: "I **worked** all day yesterday.", tr: "Dün bütün gün çalıştım." },
          { en: "She **studied** English for 3 years.", tr: "3 yıl İngilizce çalıştı." },
          { en: "They **visited** the museum.", tr: "Müzeyi ziyaret ettiler." }
        ]
      },
      {
        title: "Düzensiz Fiiller (Irregular Verbs)",
        content: `Bazı fiiller düzensizdir, ezberlenmelidir:

| Base | Past | Türkçe |
|------|------|--------|
| go | **went** | gitmek |
| come | **came** | gelmek |
| see | **saw** | görmek |
| have | **had** | sahip olmak |
| do | **did** | yapmak |
| take | **took** | almak |
| make | **made** | yapmak |
| get | **got** | almak |
| buy | **bought** | satın almak |
| eat | **ate** | yemek |`,
        examples: [
          { en: "I **went** to the airport.", tr: "Havalimanına gittim." },
          { en: "She **had** breakfast at 8.", tr: "Saat 8'de kahvaltı yaptı." },
          { en: "We **saw** a beautiful sunset.", tr: "Güzel bir gün batımı gördük." }
        ]
      },
      {
        title: "Olumsuz ve Soru",
        content: `**Olumsuz:** Özne + **did not (didn't)** + fiil (yalın hali)
**Soru:** **Did** + özne + fiil (yalın hali)?

**Önemli:** "didn't" ve "did" kullanıldığında fiil YALIN kalır, -ed EKLENMEZ!`,
        examples: [
          { en: "I **didn't go** to work yesterday.", tr: "Dün işe gitmedim." },
          { en: "She **didn't see** the message.", tr: "Mesajı görmedi." },
          { en: "**Did** you **visit** Istanbul?", tr: "İstanbul'u ziyaret ettin mi?" },
          { en: "**Did** he **call** you?", tr: "Seni aradı mı?" }
        ]
      }
    ]
  },

  "present_perfect": {
    name: "Present Perfect (have/has + V3)",
    level: "A2",
    sections: [
      {
        title: "Ne Zaman Kullanılır?",
        content: `**Present Perfect** şu durumlarda kullanılır:

• **Deneyimler:** Hayatınızda yaşadığınız/yaşamadığınız şeyler
• **Yakın geçmiş:** Az önce olmuş olaylar
• **Devam eden durumlar:** Geçmişte başlayıp hala devam eden durumlar
• **Sonucu önemli olaylar:** Ne zaman olduğu değil, sonucu önemli

**Zaman zarfları:** ever, never, already, yet, just, recently, since, for`,
        examples: [
          { en: "I have visited 10 countries.", tr: "10 ülke ziyaret ettim." },
          { en: "She has just finished her work.", tr: "İşini az önce bitirdi." },
          { en: "They have lived here for 5 years.", tr: "5 yıldır burada yaşıyorlar." }
        ]
      },
      {
        title: "Cümle Yapısı",
        content: `**Özne + have/has + Past Participle (V3)**

• I/You/We/They + **have** + V3
• He/She/It + **has** + V3

**Past Participle (V3):**
• Düzenli fiiller: work → worked, play → played
• Düzensiz fiiller: go → gone, see → seen, do → done`,
        examples: [
          { en: "I **have worked** here since 2020.", tr: "2020'den beri burada çalışıyorum." },
          { en: "She **has been** to Paris twice.", tr: "Paris'e iki kez gitti." },
          { en: "They **have finished** the project.", tr: "Projeyi bitirdiler." }
        ]
      },
      {
        title: "For vs Since",
        content: `**FOR:** Süre belirtir (ne kadar süredir)
• for 2 hours, for 5 years, for a long time

**SINCE:** Başlangıç noktası belirtir (ne zamandan beri)
• since 2020, since Monday, since I was a child`,
        examples: [
          { en: "I have known her **for** 10 years.", tr: "Onu 10 yıldır tanıyorum." },
          { en: "He has worked here **since** 2018.", tr: "2018'den beri burada çalışıyor." },
          { en: "We have been friends **for** a long time.", tr: "Uzun süredir arkadaşız." },
          { en: "She has lived in London **since** January.", tr: "Ocak'tan beri Londra'da yaşıyor." }
        ]
      },
      {
        title: "Already, Yet, Just, Ever, Never",
        content: `• **Already** (çoktan): Olumlu cümlelerde - I have **already** done it.
• **Yet** (henüz): Olumsuz ve sorularda - Have you finished **yet**?
• **Just** (az önce): I have **just** arrived.
• **Ever** (hiç): Sorularda - Have you **ever** been to Japan?
• **Never** (asla): I have **never** seen snow.`,
        examples: [
          { en: "I have **already** eaten.", tr: "Çoktan yedim." },
          { en: "She hasn't arrived **yet**.", tr: "Henüz gelmedi." },
          { en: "Have you **ever** flown a plane?", tr: "Hiç uçak uçurdun mu?" },
          { en: "I have **never** been late.", tr: "Hiç geç kalmadım." }
        ]
      }
    ]
  },

  "future_forms": {
    name: "Future Forms (will, going to)",
    level: "A2",
    sections: [
      {
        title: "WILL - Anlık kararlar ve tahminler",
        content: `**WILL** şu durumlarda kullanılır:

• **Anlık kararlar:** O an verilen kararlar
• **Tahminler:** Kişisel görüşe dayalı gelecek tahminleri
• **Söz verme:** Promise, offer, request
• **Genel gelecek:** Uzak gelecek planları

**Yapı:** Özne + **will** + fiil (yalın)
**Kısaltma:** I'll, you'll, he'll, she'll, we'll, they'll`,
        examples: [
          { en: "I'**ll** help you with that.", tr: "Sana bu konuda yardım edeceğim." },
          { en: "I think it **will** rain tomorrow.", tr: "Yarın yağmur yağacak sanırım." },
          { en: "She **will** be a great pilot.", tr: "Harika bir pilot olacak." }
        ]
      },
      {
        title: "GOING TO - Planlar ve kanıtlara dayalı tahminler",
        content: `**GOING TO** şu durumlarda kullanılır:

• **Önceden yapılmış planlar:** Daha önce karar verilmiş işler
• **Kanıtlara dayalı tahminler:** Görünen işaretlere dayalı tahminler

**Yapı:** Özne + **am/is/are + going to** + fiil`,
        examples: [
          { en: "I'**m going to** visit my parents next week.", tr: "Gelecek hafta ailemi ziyaret edeceğim." },
          { en: "Look at those clouds! It'**s going to** rain.", tr: "Şu bulutlara bak! Yağmur yağacak." },
          { en: "She'**s going to** start a new job.", tr: "Yeni bir işe başlayacak." }
        ]
      },
      {
        title: "WILL vs GOING TO Karşılaştırması",
        content: `| Durum | WILL | GOING TO |
|-------|------|----------|
| Anlık karar | ✓ I'll have coffee. | ✗ |
| Önceden plan | ✗ | ✓ I'm going to study medicine. |
| Kişisel tahmin | ✓ I think he'll win. | ✗ |
| Kanıta dayalı | ✗ | ✓ Look! It's going to fall! |`,
        examples: [
          { en: "A: We need milk. B: I'**ll** go and buy some.", tr: "A: Süte ihtiyacımız var. B: Gidip alacağım." },
          { en: "We'**re going to** get married in June.", tr: "Haziran'da evleneceğiz." }
        ]
      }
    ]
  },

  "modal_verbs": {
    name: "Modal Verbs (should, must, may)",
    level: "A2",
    sections: [
      {
        title: "SHOULD - Tavsiye",
        content: `**SHOULD** tavsiye vermek için kullanılır:

• You **should** study more. (Daha çok çalışmalısın.)
• **Should** I call him? (Onu aramalı mıyım?)
• You **shouldn't** eat too much sugar. (Çok şeker yememelisin.)

**Yapı:** Özne + should/shouldn't + fiil (yalın)`,
        examples: [
          { en: "You **should** see a doctor.", tr: "Doktora görünmelisin." },
          { en: "Passengers **should** fasten their seatbelts.", tr: "Yolcular kemerlerini bağlamalı." },
          { en: "You **shouldn't** be late.", tr: "Geç kalmamalısın." }
        ]
      },
      {
        title: "MUST - Zorunluluk",
        content: `**MUST** güçlü zorunluluk ifade eder:

• You **must** wear a uniform. (Üniforma giymelisin. - Kural)
• Passengers **must** show their passports. (Yolcular pasaport göstermeli.)

**Must not (mustn't)** = Yasak!
• You **mustn't** smoke here. (Burada sigara içemezsin.)`,
        examples: [
          { en: "You **must** arrive on time.", tr: "Zamanında gelmelisin." },
          { en: "You **mustn't** use your phone during takeoff.", tr: "Kalkış sırasında telefon kullanamazsın." },
          { en: "Crew members **must** complete training.", tr: "Ekip üyeleri eğitimi tamamlamalı." }
        ]
      },
      {
        title: "MAY/MIGHT - Olasılık",
        content: `**MAY** ve **MIGHT** olasılık ifade eder:

• It **may** rain later. (Daha sonra yağmur yağabilir.)
• She **might** be late. (Geç kalabilir.)

**May I...?** = İzin istemek (resmi)
• **May I** help you? (Size yardımcı olabilir miyim?)`,
        examples: [
          { en: "The flight **may** be delayed.", tr: "Uçuş gecikebilir." },
          { en: "**May I** see your boarding pass?", tr: "Biniş kartınızı görebilir miyim?" },
          { en: "He **might** come to the party.", tr: "Partiye gelebilir." }
        ]
      },
      {
        title: "CAN/COULD - Yetenek ve İzin",
        content: `**CAN** yetenek ve izin için kullanılır:
• I **can** speak English. (İngilizce konuşabiliyorum.)
• **Can** I open the window? (Pencereyi açabilir miyim?)

**COULD** geçmişteki yetenek veya kibarca rica:
• I **could** run fast when I was young. (Gençken hızlı koşabilirdim.)
• **Could** you help me, please? (Bana yardım eder misiniz?)`,
        examples: [
          { en: "She **can** fly a plane.", tr: "Uçak kullanabilir." },
          { en: "**Could** you repeat that?", tr: "Tekrar eder misiniz?" },
          { en: "I **couldn't** sleep last night.", tr: "Dün gece uyuyamadım." }
        ]
      }
    ]
  },

  "comparatives": {
    name: "Comparative & Superlative",
    level: "A2",
    sections: [
      {
        title: "Comparative (Karşılaştırma)",
        content: `İki şeyi karşılaştırmak için kullanılır:

**Kısa sıfatlar (1-2 hece):** sıfat + **-er** + than
• tall → tall**er** than
• big → bigg**er** than
• happy → happi**er** than

**Uzun sıfatlar (3+ hece):** **more** + sıfat + than
• beautiful → **more** beautiful than
• expensive → **more** expensive than`,
        examples: [
          { en: "She is **taller than** her sister.", tr: "Kız kardeşinden daha uzun." },
          { en: "This hotel is **more expensive than** that one.", tr: "Bu otel diğerinden daha pahalı." },
          { en: "Flying is **faster than** driving.", tr: "Uçmak, araba sürmekten daha hızlı." }
        ]
      },
      {
        title: "Superlative (En üstünlük)",
        content: `Bir grupta en üstün olanı belirtir:

**Kısa sıfatlar:** **the** + sıfat + **-est**
• tall → **the tallest**
• big → **the biggest**

**Uzun sıfatlar:** **the most** + sıfat
• beautiful → **the most** beautiful
• important → **the most** important`,
        examples: [
          { en: "She is **the tallest** in the class.", tr: "Sınıfın en uzunu." },
          { en: "This is **the most beautiful** city I've seen.", tr: "Gördüğüm en güzel şehir." },
          { en: "Safety is **the most important** thing.", tr: "Güvenlik en önemli şeydir." }
        ]
      },
      {
        title: "Düzensiz Karşılaştırmalar",
        content: `Bazı sıfatlar düzensizdir:

| Sıfat | Comparative | Superlative |
|-------|-------------|-------------|
| good | **better** | **the best** |
| bad | **worse** | **the worst** |
| far | **farther/further** | **the farthest/furthest** |
| little | **less** | **the least** |
| much/many | **more** | **the most** |`,
        examples: [
          { en: "This is **better than** the old one.", tr: "Bu eskisinden daha iyi." },
          { en: "She is **the best** student.", tr: "En iyi öğrenci." },
          { en: "The weather is **worse** today.", tr: "Bugün hava daha kötü." }
        ]
      }
    ]
  },

  "basic_questions": {
    name: "Basic Questions (Wh-)",
    level: "A1",
    sections: [
      {
        title: "Soru Kelimeleri",
        content: `İngilizce'de temel soru kelimeleri:

• **What** - Ne? (What is your name?)
• **Where** - Nerede? (Where do you live?)
• **When** - Ne zaman? (When is your birthday?)
• **Who** - Kim? (Who is that?)
• **Why** - Neden? (Why are you late?)
• **How** - Nasıl? (How are you?)
• **Which** - Hangisi? (Which one do you want?)
• **Whose** - Kimin? (Whose bag is this?)`,
        examples: [
          { en: "**What** do you do?", tr: "Ne iş yapıyorsun?" },
          { en: "**Where** is the airport?", tr: "Havalimanı nerede?" },
          { en: "**When** does the flight leave?", tr: "Uçuş ne zaman kalkıyor?" }
        ]
      },
      {
        title: "How ile Sorular",
        content: `**How** farklı kelimelerle birleşerek özel anlamlar kazanır:

• **How much** - Ne kadar? (sayılamayan) - How much water?
• **How many** - Kaç tane? (sayılabilen) - How many people?
• **How old** - Kaç yaşında? - How old are you?
• **How long** - Ne kadar süre? - How long is the flight?
• **How often** - Ne sıklıkla? - How often do you travel?
• **How far** - Ne kadar uzak? - How far is the hotel?`,
        examples: [
          { en: "**How much** does it cost?", tr: "Ne kadar?" },
          { en: "**How many** passengers are there?", tr: "Kaç yolcu var?" },
          { en: "**How long** is the flight?", tr: "Uçuş ne kadar sürüyor?" }
        ]
      }
    ]
  },

  "plurals_articles": {
    name: "Plurals & Articles (a/an/the)",
    level: "A1",
    sections: [
      {
        title: "A vs AN",
        content: `**A** ve **AN** belirsiz artikel olarak kullanılır:

• **A** - Ünsüz sesle başlayan kelimelerden önce
  - a book, a car, a university (üniversiti sesi ünsüz)
  
• **AN** - Ünlü sesle başlayan kelimelerden önce
  - an apple, an hour (h sessiz), an umbrella

**Dikkat:** Yazılışa değil, SESE bakılır!`,
        examples: [
          { en: "I need **a** ticket.", tr: "Bir bilete ihtiyacım var." },
          { en: "She is **an** English teacher.", tr: "O bir İngilizce öğretmeni." },
          { en: "It takes **an** hour.", tr: "Bir saat sürüyor." }
        ]
      },
      {
        title: "THE - Belirli Artikel",
        content: `**THE** belirli, bilinen şeyler için kullanılır:

• Daha önce bahsedilen: I saw **a** car. **The** car was red.
• Tek olan şeyler: **the** sun, **the** moon, **the** sky
• Üstünlük: **the** best, **the** tallest
• Belirli yerler: **the** airport, **the** hotel (belli olan)`,
        examples: [
          { en: "**The** captain will make an announcement.", tr: "Kaptan anons yapacak." },
          { en: "Please go to **the** gate.", tr: "Lütfen kapıya gidin." },
          { en: "**The** sun is shining.", tr: "Güneş parlıyor." }
        ]
      },
      {
        title: "Çoğul Yapımı",
        content: `Tekil isimleri çoğul yapmak için genellikle **-s** eklenir:

• book → book**s**
• car → car**s**

**Özel durumlar:**
• -s, -x, -ch, -sh → +**es**: bus → bus**es**, box → box**es**
• -y → **ies**: city → cit**ies** (ünsüz+y)
• -f/-fe → **ves**: knife → kni**ves**, leaf → lea**ves**
• Düzensiz: man → **men**, woman → **women**, child → **children**`,
        examples: [
          { en: "Two **passengers** are waiting.", tr: "İki yolcu bekliyor." },
          { en: "There are many **boxes**.", tr: "Birçok kutu var." },
          { en: "The **children** are playing.", tr: "Çocuklar oynuyor." }
        ]
      }
    ]
  },

  "can_cant": {
    name: "Can/Can't (ability)",
    level: "A1",
    sections: [
      {
        title: "CAN - Yetenek ve İzin",
        content: `**CAN** şu durumlarda kullanılır:

• **Yetenek:** Bir şeyi yapabilme becerisi
• **İzin:** Bir şeye izin verilmesi
• **Olasılık:** Teorik olarak mümkün olan

**Yapı:** Özne + **can** + fiil (yalın hali)`,
        examples: [
          { en: "I **can** speak English.", tr: "İngilizce konuşabiliyorum." },
          { en: "**Can** I help you?", tr: "Size yardımcı olabilir miyim?" },
          { en: "You **can** park here.", tr: "Buraya park edebilirsiniz." }
        ]
      },
      {
        title: "CAN'T - Yapamamak",
        content: `**CAN'T** (cannot) olumsuz durumlar için:

• Yapamamak, becerememek
• İzin verilmemesi
• Olanaksızlık

**Yapı:** Özne + **can't** + fiil (yalın hali)`,
        examples: [
          { en: "I **can't** swim.", tr: "Yüzemiyorum." },
          { en: "You **can't** smoke here.", tr: "Burada sigara içemezsiniz." },
          { en: "She **can't** come today.", tr: "Bugün gelemez." }
        ]
      }
    ]
  },

  "adverbs_frequency": {
    name: "Adverbs of Frequency",
    level: "A2",
    sections: [
      {
        title: "Sıklık Zarfları",
        content: `Sıklık zarfları eylemin ne sıklıkla yapıldığını belirtir:

| Zarf | Sıklık | Türkçe |
|------|--------|--------|
| **always** | 100% | her zaman |
| **usually** | 80% | genellikle |
| **often** | 60% | sık sık |
| **sometimes** | 40% | bazen |
| **rarely/seldom** | 20% | nadiren |
| **never** | 0% | asla |`,
        examples: [
          { en: "I **always** arrive early.", tr: "Her zaman erken gelirim." },
          { en: "She **usually** drinks coffee.", tr: "Genellikle kahve içer." },
          { en: "They **never** miss a flight.", tr: "Hiç uçuş kaçırmazlar." }
        ]
      },
      {
        title: "Cümledeki Yeri",
        content: `Sıklık zarfları genellikle:

• **Be fiilinden SONRA:** She is **always** happy.
• **Ana fiilden ÖNCE:** I **usually** wake up early.
• **Yardımcı fiil ile ana fiil arasında:** She can **never** remember.

**Sometimes** cümle başında veya sonunda da olabilir.`,
        examples: [
          { en: "He **is always** on time.", tr: "Her zaman zamanında." },
          { en: "We **often** travel by plane.", tr: "Sık sık uçakla seyahat ederiz." },
          { en: "**Sometimes** I work late.", tr: "Bazen geç çalışırım." }
        ]
      }
    ]
  }
};

const GrammarLesson = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { level, topic } = useParams();
  const [currentSection, setCurrentSection] = useState(0);
  const [completedSections, setCompletedSections] = useState([]);
  const [showExamples, setShowExamples] = useState({});
  
  const lesson = GRAMMAR_LESSONS[topic];

  // Demo kullanıcıları bu sayfaya erişemez
  useEffect(() => {
    if (user?.is_demo) {
      toast.error('Bu özellik tam sürümde mevcuttur');
      navigate('/dashboard');
    }
  }, [user, navigate]);
  
  // Reset when topic changes - this is intentional as we need to reset UI state
  // when the user navigates to a different topic
  useEffect(() => {
    setCurrentSection(0);
    setCompletedSections([]);
    setShowExamples({});
  }, [topic]);
  
  if (!lesson) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Ders Bulunamadı</h2>
              <p className="text-gray-600 mb-4">Bu konu için henüz ders içeriği hazırlanmamış.</p>
              <Button onClick={() => navigate('/exercises')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Egzersizlere Dön
              </Button>
            </CardContent>
          </Card>
        </div>
      </CabinCrewLayout>
    );
  }
  
  const totalSections = lesson.sections.length;
  const progress = ((currentSection + 1) / totalSections) * 100;
  const section = lesson.sections[currentSection];
  
  const handleNext = () => {
    if (!completedSections.includes(currentSection)) {
      setCompletedSections([...completedSections, currentSection]);
    }
    
    if (currentSection < totalSections - 1) {
      setCurrentSection(currentSection + 1);
    }
  };
  
  const handlePrev = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };
  
  const handleStartExercise = () => {
    navigate(`/exercises/grammar/${level}/${topic}`);
  };
  
  const toggleExamples = (idx) => {
    setShowExamples(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };
  
  // Format content with markdown-like syntax
  const formatContent = (content) => {
    return content
      .split('\n')
      .map((line, idx) => {
        // Bold text
        line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        
        // Bullet points
        if (line.startsWith('• ')) {
          return `<li key="${idx}" class="ml-4">${line.substring(2)}</li>`;
        }
        
        // Tables (simplified)
        if (line.startsWith('|')) {
          return null; // Skip table formatting for now
        }
        
        return `<p key="${idx}" class="mb-2">${line}</p>`;
      })
      .filter(Boolean)
      .join('');
  };
  
  return (
    <CabinCrewLayout>
      <div className="min-h-screen py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/exercises')}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Egzersizlere Dön
            </Button>
            
            <div className="flex items-center gap-3 mb-2">
              <Badge className="bg-blue-100 text-blue-700">{level}</Badge>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
                {lesson.name}
              </h1>
            </div>
            
            {/* Progress */}
            <div className="flex items-center gap-4 mt-4">
              <Progress value={progress} className="flex-1 h-3" />
              <span className="text-sm font-medium text-gray-600">
                {currentSection + 1} / {totalSections}
              </span>
            </div>
          </div>
          
          {/* Section Navigation */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {lesson.sections.map((sec, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSection(idx)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  currentSection === idx
                    ? 'bg-blue-600 text-white'
                    : completedSections.includes(idx)
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {completedSections.includes(idx) && (
                  <CheckCircle className="w-4 h-4" />
                )}
                {sec.title}
              </button>
            ))}
          </div>
          
          {/* Content Card */}
          <Card className="border-2 border-blue-200 shadow-xl mb-6">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
              <CardTitle className="flex items-center gap-3 text-xl">
                <BookOpen className="w-6 h-6 text-blue-600" />
                {section.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {/* Main Content */}
              <div 
                className="prose prose-lg max-w-none mb-6"
                dangerouslySetInnerHTML={{ __html: formatContent(section.content) }}
              />
              
              {/* Examples */}
              {section.examples && section.examples.length > 0 && (
                <div className="mt-6">
                  <button
                    onClick={() => toggleExamples(currentSection)}
                    className="flex items-center gap-2 text-blue-600 font-medium mb-4 hover:text-blue-700"
                  >
                    <Lightbulb className="w-5 h-5" />
                    <span>Örnekler ({section.examples.length})</span>
                    {showExamples[currentSection] ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                  
                  {showExamples[currentSection] && (
                    <div className="space-y-3 animate-in slide-in-from-top-2">
                      {section.examples.map((ex, idx) => (
                        <div 
                          key={idx}
                          className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100"
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold shrink-0">
                              {idx + 1}
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-gray-800 mb-1">
                                {ex.en}
                              </p>
                              <p className="text-gray-600 text-sm">
                                {ex.tr}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                if ('speechSynthesis' in window) {
                                  const utterance = new SpeechSynthesisUtterance(ex.en);
                                  utterance.lang = 'en-US';
                                  speechSynthesis.speak(utterance);
                                }
                              }}
                            >
                              <Volume2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Navigation Buttons */}
          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={handlePrev}
              disabled={currentSection === 0}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Önceki
            </Button>
            
            {currentSection === totalSections - 1 ? (
              <Button
                onClick={handleStartExercise}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white flex items-center gap-2 px-8"
              >
                <Sparkles className="w-5 h-5" />
                Egzersize Başla
                <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white flex items-center gap-2"
              >
                Sonraki
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}
          </div>
          
          {/* Quick tip */}
          {currentSection === totalSections - 1 && (
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-start gap-3">
                <Brain className="w-6 h-6 text-amber-600 shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-amber-800">Egzersiz Zamanı!</h4>
                  <p className="text-amber-700 text-sm">
                    Dersi tamamladın! Şimdi öğrendiklerini egzersizlerle pekiştir.
                    Egzersizlerde bu konuya özel sorular çözeceksin.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </CabinCrewLayout>
  );
};

export default GrammarLesson;
